/* eslint-disable jsx-a11y/anchor-is-valid */
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";

import RestartAlt from '@mui/icons-material/RestartAlt';
import Tooltip from "@mui/material/Tooltip";
import { Table } from 'antd';
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import moment from "moment";
import { useForm } from "react-hook-form";
import LogicContext from "../../Context/LogicContext";

import { voucherService } from "../../services/voucherService";
import { SetGeneralVoucherListAtom, GetGeneralVoucherListAtom } from "../../store/voucherAtom";

// ────────────────────────────────────────────────
//           EXPORT LIBRARIES
// ────────────────────────────────────────────────
import Papa from "papaparse";                    // CSV
import * as XLSX from "xlsx";                    // Excel

// ... rest of your imports

function VouchersMainPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logicContext = useContext(LogicContext);

  const getGeneralVoucherList = GetGeneralVoucherListAtom();
  const setGeneralVoucherList = SetGeneralVoucherListAtom();

  const [filteredData, setFilteredData] = useState([]);

  const { register, getValues } = useForm();

  var url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];




  useEffect(() => {
    document.title = 'Voucher Page';
    if (getGeneralVoucherList.length < 1) {
      loadVoucherData();
    } else {
      setFilteredData(getGeneralVoucherList);
    }
  }, [getGeneralVoucherList]);

  const loadVoucherData = () => {
    voucherService.getVoucherList().then(res => {
      if (res.data?.voucher_info?.length) {
        setGeneralVoucherList(res.data.voucher_info);
        setFilteredData(res.data.voucher_info);
      }
    });
  };

  // Your columns (we'll use them for export headers)
  const columns = [
    { title: 'Ref', dataIndex: 'Ref_No', key: 'Ref_No' },
    { title: 'Customer Name', dataIndex: 'Customer_Full_Name' },
    { title: 'Customer No', dataIndex: 'Customer_Mobile_No' },
    { title: 'Status', dataIndex: 'Voucher_Status_Name' },
    { title: 'Voucher No By', dataIndex: 'Voucher_Number' },
    { title: 'Voucher Amount', dataIndex: 'Voucher_Amount' },
    { title: 'Type', dataIndex: 'Voucher_Type_Name' },
    { title: 'Recipient', dataIndex: 'Reciepient_Name' },
    { title: 'Recipient No', dataIndex: 'Reciepient_Mobile_Number' },
  ];

  // ────────────────────────────────────────────────
  //                EXPORT HANDLERS
  // ────────────────────────────────────────────────

  const exportToCSV = () => {
    if (!filteredData?.length) {
      logicContext.showToast("No data to export", "warning");
      return;
    }

    const headers = columns.map(col => col.title);
    const data = filteredData.map(row => ({
      "Ref": row.Ref_No,
      "Customer Name": row.Customer_Full_Name || "",
      "Customer No": row.Customer_Mobile_No || "",
      "Status": row.Voucher_Status_Name || "",
      "Voucher No By": row.Voucher_Number || "",
      "Voucher Amount": formatCustomNumber(row.Voucher_Amount),
      "Type": row.Voucher_Type_Name || "",
      "Recipient": row.Reciepient_Name || "",
      "Recipient No": row.Reciepient_Mobile_Number || "",
    }));

    const csv = Papa.unparse([headers, ...data.map(Object.values)], {
      delimiter: ",",
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `vouchers_${moment().format("YYYY-MM-DD_HHmm")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    logicContext.showToast("CSV exported successfully", "success");
  };

  const exportToExcel = () => {
    if (!filteredData?.length) {
      logicContext.showToast("No data to export", "warning");
      return;
    }

    const data = filteredData.map(row => ({
      "Ref": row.Ref_No,
      "Customer Name": row.Customer_Full_Name || "",
      "Customer No": row.Customer_Mobile_No || "",
      "Status": row.Voucher_Status_Name || "",
      "Voucher No By": row.Voucher_Number || "",
      "Voucher Amount": formatCustomNumber(row.Voucher_Amount),
      "Type": row.Voucher_Type_Name || "",
      "Recipient": row.Reciepient_Name || "",
      "Recipient No": row.Reciepient_Mobile_Number || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vouchers");

    // Optional: auto-size columns (a bit better look)
    const colWidths = headers => headers.map(h => ({ wch: Math.max(12, h.length + 4) }));
    ws['!cols'] = colWidths(Object.keys(data[0] || {}));

    XLSX.writeFile(wb, `vouchers_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`);

    logicContext.showToast("Excel file exported", "success");
  };

  // ─── Your searchByDate remains the same ───
  const searchByDate = async () => {
    const formData = getValues();
    const response = await voucherService.voucherByDate(formData);
    if (response?.data?.length) {
      setFilteredData(response.data);
    } else {
      setFilteredData([]);
      logicContext.showToast("No available record for your search query!", "");
    }
  };

  return (
    <>
      <section>
        <div className="w-full lg:flex justify-start">
          <section className="w-full">
            <section className="bg-white py-4 px-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 z-10">
                <Tooltip title="Reload">
                  <RestartAlt
                    className="cursor-pointer ml-2"
                    onClick={() => loadVoucherData()}
                    color="primary"
                    fontSize="large"
                  />
                </Tooltip>

                {/* Date Range Search */}
                <div className="flex items-center gap-3 z-10">
                  <div className="flex flex-col mb-3">
                    <label className="text-sm font-medium text-gray-700">From Date</label>
                    <input
                      defaultValue={moment().format("YYYY-MM-DD")}
                      {...register("from_date")}
                      type="date"
                      className="h-10 w-80 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    />
                  </div>
                  <div className="flex flex-col mb-3">
                    <label className="text-sm font-medium text-gray-700">To Date</label>
                    <input
                      defaultValue={moment().format("YYYY-MM-DD")}
                      {...register("to_date")}
                      type="date"
                      className="h-10 w-80 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    />
                  </div>

                  <button
                    onClick={searchByDate}
                    type="button"
                    className="h-10 bg-blue-600 text-white text-sm font-medium rounded-md px-4 py-2 hover:bg-blue-700 transition-colors"
                  >
                    Search
                  </button>
                </div>
              </div>

              {/* EXPORT BUTTONS */}
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  <button
                    onClick={exportToCSV}
                    disabled={!filteredData?.length}
                    className="h-10 font-medium text-sm rounded-md px-4 py-2 bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Export CSV
                  </button>

                  <button
                    onClick={exportToExcel}
                    disabled={!filteredData?.length}
                    className="h-10 font-medium text-sm rounded-md px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Export Excel
                  </button>
                </div>
              </div>
            </section>

            <section className="mt-5">
              <GeneralCustomTable
                tableHeadList={columns}
                tableDataList={filteredData}
                allowCheckBox={false}
                paginationOptions={{
                  pageSize: 10,
                  showSizeChanger: true,
                  total: filteredData.length,
                }}
                rowKey={(record) => record.Ref_No || record.Payment_Ref_No}
                handleRowClick={(record) =>
                  navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${record?.Ref_No}`)
                }
              />
            </section>
          </section>
        </div>
      </section>
    </>
  );
}

export default VouchersMainPage;